<template>
    <div class="pricing-analytics">
        <a-spin :spinning="loading">
            <div v-if="!loading && data && data.pricing_analytics">
                <div class="toolbar">
                    <div class="buttons">
                        <IconButton
                            :classNames="viewMode === 'chart' ? 'selected' : ''"
                            :onClick="() => (viewMode = 'chart')"
                            v-tippy="{ content: $t('whatsapp_business_api.metaInsightsViewChart') }"
                        >
                            <md-icon>insights</md-icon>
                        </IconButton>
                        <IconButton
                            :classNames="viewMode === 'pie' ? 'selected' : ''"
                            :onClick="() => (viewMode = 'pie')"
                            v-tippy="{ content: $t('whatsapp_business_api.metaInsightsPieChart') }"
                        >
                            <md-icon>donut_small</md-icon>
                        </IconButton>
                        <IconButton
                            :classNames="viewMode === 'table' ? 'selected' : ''"
                            :onClick="() => (viewMode = 'table')"
                            v-tippy="{ content: $t('whatsapp_business_api.metaInsightsViewTable') }"
                        >
                            <md-icon>table_view</md-icon>
                        </IconButton>
                        <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('whatsapp_business_api.metaInsightsDownloadExcel') }">
                            <md-icon>download</md-icon>
                        </IconButton>
                    </div>
                </div>

                <div class="stats-row">
                    <StatCard :title="$t('whatsapp_business_api.metaInsightsTotalVolume')" :value="totalVolume" :unit="$t('whatsapp_business_api.metaInsightsUnitMessage')" />
                    <StatCard :title="$t('whatsapp_business_api.metaInsightsTotalCost')" :value="totalCost.toFixed(2)" unit="$" />
                    <StatCard :title="$t('whatsapp_business_api.metaInsightsAvgUnitCost')" :value="avgCostPerMessage.toFixed(4)" :unit="$t('whatsapp_business_api.metaInsightsCostPerMessage')" />
                </div>

                <div class="pricing-breakdown">
                    <h3>{{ $t('whatsapp_business_api.metaInsightsPricingCategoryBreakdown') }}</h3>
                    <div class="pricing-cards">
                        <div v-for="(value, category) in pricingCategoryBreakdown" :key="category" class="pricing-card">
                            <div class="pricing-name">{{ getPricingCategoryLabel(category) }}</div>
                            <div class="pricing-volume">{{ value.volume }} {{ $t('whatsapp_business_api.metaInsightsUnitMessage') }}</div>
                            <div class="pricing-cost">${{ value.cost.toFixed(2) }}</div>
                        </div>
                    </div>
                </div>

                <div class="pricing-type-breakdown">
                    <h3>{{ $t('whatsapp_business_api.metaInsightsPricingTypeBreakdown') }}</h3>
                    <div class="pricing-cards">
                        <div v-for="(value, type) in pricingTypeBreakdown" :key="type" class="pricing-card">
                            <div class="pricing-name">{{ getPricingTypeLabel(type) }}</div>
                            <div class="pricing-volume">{{ value.volume }} {{ $t('whatsapp_business_api.metaInsightsUnitMessage') }}</div>
                            <div class="pricing-cost">${{ value.cost.toFixed(2) }}</div>
                        </div>
                    </div>
                </div>

                <div class="chart-container" v-if="viewMode === 'chart'">
                    <StackedAreaChart
                        :title="$t('whatsapp_business_api.metaInsightsPricingStats')"
                        :x-axis-data="getXAxisData()"
                        :legends="getPricingLegends()"
                        :colors="pricingColors"
                        :series="getPricingSeries()"
                    />
                </div>

                <div class="pie-container" v-if="viewMode === 'pie'">
                    <PieChartV2 :title="$t('whatsapp_business_api.metaInsightsCostDistribution')" :legends="getPricingLegends()" :series="getPieSeries()" />
                </div>

                <div class="table-container" v-if="viewMode === 'table'">
                    <a-table :columns="columns" :data-source="tableData" :pagination="{ pageSize: 10 }" size="middle" />
                </div>
            </div>

            <div v-else-if="!loading" class="empty-state">
                <a-empty :description="$t('whatsapp_business_api.metaInsightsNoData')" />
            </div>
        </a-spin>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import { expertToExcel } from "../../../../../utils/utils";
import StackedAreaChart from "../../../Chatbot/pages/reports/charts/StackedAreaChart.vue";
import PieChartV2 from "../../../Chatbot/pages/reports/charts/PieChartV2.vue";
import StatCard from "../../../Chatbot/pages/reports/charts/StatCard.vue";
import IconButton from "../../../Settings/components/IconButton.vue";

export default Vue.extend({
    name: "PricingAnalytics",
    components: {
        StackedAreaChart,
        PieChartV2,
        StatCard,
        IconButton,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
        filters: {
            type: Object,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            viewMode: "chart",
            pricingColors: ["#80FFA5", "#00DDFF", "#37A2FF", "#FF0087", "#FFBF00", "#C070FF"],
            pricingCategoryLabels: {
                AUTHENTICATION: this.$t("whatsapp_business_api.metaInsightsPricingCategoryAuth"),
                AUTHENTICATION_INTERNATIONAL: this.$t("whatsapp_business_api.metaInsightsPricingCategoryAuthInternational"),
                MARKETING: this.$t("whatsapp_business_api.metaInsightsPricingCategoryMarketing"),
                SERVICE: this.$t("whatsapp_business_api.metaInsightsPricingCategoryService"),
                UTILITY: this.$t("whatsapp_business_api.metaInsightsPricingCategoryUtility"),
                REFERRAL_CONVERSION: this.$t("whatsapp_business_api.metaInsightsPricingCategoryReferral"),
            },
            pricingTypeLabels: {
                REGULAR: this.$t("whatsapp_business_api.metaInsightsPricingTypeRegular"),
                FREE_CUSTOMER_SERVICE: this.$t("whatsapp_business_api.metaInsightsPricingTypeFreeCustomerService"),
                FREE_ENTRY_POINT: this.$t("whatsapp_business_api.metaInsightsPricingTypeFreeEntryPoint"),
            },
            columns: [
                {
                    title: this.$t("whatsapp_business_api.metaInsightsDateColumn"),
                    dataIndex: "date",
                    key: "date",
                },
                {
                    title: this.$t("whatsapp_business_api.metaInsightsCountry"),
                    dataIndex: "country",
                    key: "country",
                },
                {
                    title: this.$t("whatsapp_business_api.metaInsightsCategory"),
                    dataIndex: "category",
                    key: "category",
                },
                {
                    title: this.$t("whatsapp_business_api.metaInsightsType"),
                    dataIndex: "type",
                    key: "type",
                },
                {
                    title: this.$t("whatsapp_business_api.metaInsightsVolume"),
                    dataIndex: "volume",
                    key: "volume",
                    sorter: (a, b) => a.volume - b.volume,
                },
                {
                    title: this.$t("whatsapp_business_api.metaInsightsCost"),
                    dataIndex: "cost",
                    key: "cost",
                    sorter: (a, b) => parseFloat(a.costRaw) - parseFloat(b.costRaw),
                },
                {
                    title: this.$t("whatsapp_business_api.metaInsightsTier"),
                    dataIndex: "tier",
                    key: "tier",
                },
            ],
        };
    },
    computed: {
        dataPoints() {
            const data = _.get(this.data, "pricing_analytics.data", []) || [];
            if (data.length > 0) {
                return _.get(data, "0.data_points", []) || [];
            }
            return [];
        },
        totalVolume() {
            return this.dataPoints.reduce((sum, dp) => sum + (dp.volume || 0), 0);
        },
        totalCost() {
            return this.dataPoints.reduce((sum, dp) => sum + (dp.cost || 0), 0);
        },
        avgCostPerMessage() {
            if (this.totalVolume === 0) return 0;
            return this.totalCost / this.totalVolume;
        },
        pricingCategoryBreakdown() {
            const breakdown = {};
            this.dataPoints.forEach((dp) => {
                const category = dp.pricing_category || "OTHER";
                if (!breakdown[category]) {
                    breakdown[category] = { volume: 0, cost: 0 };
                }
                breakdown[category].volume += dp.volume || 0;
                breakdown[category].cost += dp.cost || 0;
            });
            return breakdown;
        },
        pricingTypeBreakdown() {
            const breakdown = {};
            this.dataPoints.forEach((dp) => {
                const type = dp.pricing_type || "OTHER";
                if (!breakdown[type]) {
                    breakdown[type] = { volume: 0, cost: 0 };
                }
                breakdown[type].volume += dp.volume || 0;
                breakdown[type].cost += dp.cost || 0;
            });
            return breakdown;
        },
        tableData() {
            return this.dataPoints.map((dp, index) => ({
                key: index,
                date: moment.unix(dp.start).format("DD/MM/YYYY"),
                country: dp.country || "-",
                category: this.getPricingCategoryLabel(dp.pricing_category),
                type: this.getPricingTypeLabel(dp.pricing_type),
                volume: dp.volume || 0,
                cost: "$" + (dp.cost || 0).toFixed(2),
                costRaw: dp.cost || 0,
                tier: dp.tier || "-",
            }));
        },
    },
    methods: {
        getPricingCategoryLabel(category) {
            return this.pricingCategoryLabels[category] || category || this.$t("whatsapp_business_api.metaInsightsConversationCategoryOther");
        },
        getPricingTypeLabel(type) {
            return this.pricingTypeLabels[type] || type || this.$t("whatsapp_business_api.metaInsightsConversationCategoryOther");
        },
        getPricingLegends() {
            return Object.keys(this.pricingCategoryBreakdown).map((cat) => this.getPricingCategoryLabel(cat));
        },
        getXAxisData() {
            const dates = _.uniq(this.dataPoints.map((dp) => moment.unix(dp.start).format("DD/MM")));
            return dates;
        },
        getPricingSeries() {
            const categories = Object.keys(this.pricingCategoryBreakdown);
            const dates = _.uniq(this.dataPoints.map((dp) => moment.unix(dp.start).format("DD/MM")));

            return categories.map((category, idx) => {
                const data = dates.map((date) => {
                    const points = this.dataPoints.filter(
                        (dp) => moment.unix(dp.start).format("DD/MM") === date && dp.pricing_category === category
                    );
                    return points.reduce((sum, p) => sum + (p.cost || 0), 0);
                });

                return {
                    name: this.getPricingCategoryLabel(category),
                    type: "line",
                    stack: "Total",
                    smooth: true,
                    lineStyle: { width: 0 },
                    showSymbol: false,
                    areaStyle: { opacity: 0.8 },
                    emphasis: { focus: "series" },
                    data: data,
                };
            });
        },
        getPieSeries() {
            return [
                {
                    name: this.$t("whatsapp_business_api.metaInsightsCostDistribution"),
                    type: "pie",
                    radius: ["40%", "70%"],
                    avoidLabelOverlap: false,
                    label: {
                        show: true,
                        formatter: "{b}: ${c} ({d}%)",
                    },
                    data: Object.entries(this.pricingCategoryBreakdown).map(([category, value]) => ({
                        name: this.getPricingCategoryLabel(category),
                        value: parseFloat(value.cost.toFixed(2)),
                    })),
                },
            ];
        },
        exportToExcel() {
            const exportData = this.tableData.map((row) => ({
                [this.$t("whatsapp_business_api.metaInsightsDateColumn")]: row.date,
                [this.$t("whatsapp_business_api.metaInsightsCountry")]: row.country,
                [this.$t("whatsapp_business_api.metaInsightsCategory")]: row.category,
                [this.$t("whatsapp_business_api.metaInsightsType")]: row.type,
                [this.$t("whatsapp_business_api.metaInsightsVolume")]: row.volume,
                [this.$t("whatsapp_business_api.metaInsightsCost")]: row.cost,
                [this.$t("whatsapp_business_api.metaInsightsTier")]: row.tier,
            }));
            expertToExcel(exportData, "pricing_analytics.xlsx", this.$t("whatsapp_business_api.metaInsightsTabPricing"));
        },
    },
});
</script>

<style scoped lang="scss">
.pricing-analytics {
    padding: 20px;

    .toolbar {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 20px;

        .buttons {
            display: flex;
            border: 1px solid var(--color-border-default);
            border-radius: 4px;

            .icon-button {
                padding: 8px;
                display: flex;
                justify-content: center;
                align-items: center;
                border-right: 1px solid var(--color-border-default);
                border-radius: unset !important;

                &.selected {
                    background-color: var(--color-bg-info-low-hover);
                }

                &:last-child {
                    border-right: none;
                }

                &:hover {
                    background-color: var(--color-bg-neutral-pressed);
                }
            }
        }
    }

    .stats-row {
        display: flex;
        gap: 20px;
        margin-bottom: 30px;
        flex-wrap: wrap;

        .stat-card {
            flex: 1;
            min-width: 200px;
        }
    }

    .pricing-breakdown,
    .pricing-type-breakdown {
        margin-bottom: 30px;

        h3 {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 15px;
        }

        .pricing-cards {
            display: flex;
            gap: 15px;
            flex-wrap: wrap;

            .pricing-card {
                padding: 15px 20px;
                background: var(--color-bg-subtle);
                border-radius: 8px;
                min-width: 150px;

                .pricing-name {
                    font-weight: 600;
                    margin-bottom: 8px;
                    color: var(--color-text-strong-default);
                    font-size: 13px;
                }

                .pricing-volume {
                    font-size: 18px;
                    font-weight: 700;
                    color: var(--color-text-info);
                }

                .pricing-cost {
                    font-size: 14px;
                    color: var(--color-text-default);
                    margin-top: 4px;
                }
            }
        }
    }

    .chart-container,
    .pie-container {
        min-height: 400px;
        margin-bottom: 30px;
    }

    .table-container {
        margin-bottom: 30px;
    }

    .empty-state {
        padding: 60px 20px;
        text-align: center;
    }
}
</style>
